import { TComponentProps } from "@/types";
import { ImgClient } from "./img-client";

/**
 * A server component wrapper around the `ImgClient` component which provides automatic LQIP generation.
 *
 * This component can only be used in server components - if you need to use an image in a client component, use the `Img.Client` component instead.
 *
 * Example usage:
 *
 * Default server component with automatic LQIP generation:
 * <Img
 *  src="https://cdn.sanity.io/images/4bgok82s/monorepo-static-assets/a9b94a7b2a65c61fc340c0378cee8f8b6a62dc9b-1600x1600.webp"
 *  alt="Alt Text"
 *  fill
 * />
 *
 * <Img.Client
 *  src="https://cdn.sanity.io/images/4bgok82s/monorepo-static-assets/a9b94a7b2a65c61fc340c0378cee8f8b6a62dc9b-1600x1600.webp"
 *  alt="Alt Text"
 *  fill
 *  lqip=[lqip image src or base64 data URL]
 * />
 *
 * <Img.Client
 *  src="https://cdn.sanity.io/images/4bgok82s/monorepo-static-assets/a9b94a7b2a65c61fc340c0378cee8f8b6a62dc9b-1600x1600.webp"
 *  alt="Alt Text"
 *  fill
 *  className="bg-blue" // Optional: use a background colour instead of LQIP
 * />
 *
 */

export type ImgProps = React.ImgHTMLAttributes<HTMLImageElement> &
  Omit<TComponentProps, "children"> & {
    fill?: boolean;
    lqip?: string;
    src: string;
    alt: string;
    width?: number;
    height?: number;
    priority?: boolean;
    disableFadeIn?: boolean;
    sizes?: string;
    imgClassName?: string;
    lqipClassName?: string;
  };

export const Img = async ({ src, ...props }: ImgProps) => {
  let lqip = props.lqip;

  // We only want to generate the LQIP if it's not provided and we're on the server
  if (!lqip && typeof window === "undefined") {
    const { generateBase64 } = await import("#actions/generateBase64");
    lqip = await generateBase64(src);
  }

  return <ImgClient src={src} {...props} lqip={lqip} />;
};

Img.Client = ImgClient;
